import { useMemo } from 'react'

import type { Bracket, BracketTeam, RGNEvent } from '@/lib/api/schemas'

/**
 * Render-name for a bracket team. The schema carries `teamLabel`
 * (e.g. "TEAM A") + a member list; for solo tournaments the team has
 * exactly one member and the spectator-friendly name is the player's
 * displayName, not "TEAM A". This helper picks the right one.
 */
function teamDisplayName(team: BracketTeam | null | undefined): string {
  if (!team) return '—'
  if (team.teamLabel && team.teamLabel.trim().length > 0) return team.teamLabel
  const members = team.members ?? []
  if (members.length === 1) {
    return members[0].displayName ?? members[0].callsign ?? 'Player'
  }
  if (members.length > 1) {
    return members[0].displayName ?? members[0].callsign ?? 'Team'
  }
  return 'Team'
}

/**
 * Tournament Stadium — bottom ticker.
 *
 * Two content sources, surfaced in priority order:
 *   1. NEXT UP — matches that are SCHEDULED or AWAITING_CONFIRMATION
 *      with the soonest scheduledAt. Primary blue.
 *   2. RESULTS — recently completed matches (winner + opponent).
 *      Subtle background.
 *
 * Auto-built from the bracket data — no separate ticker state to
 * maintain. The marquee CSS animation handles the scroll; the
 * underlying list is rebuilt on every poll tick which is harmless
 * because the animation is keyframe-driven (no JS, no re-trigger).
 *
 * Duplicated DOM for the seamless loop: render the items twice so the
 * marquee can translate -50% without ever leaving a gap. Each child
 * of `.stadium-ticker-track` therefore appears in the DOM twice.
 */
export function StadiumTicker({
  bracket,
  event,
}: {
  bracket: Bracket | null
  event: RGNEvent | null
}) {
  const items = useTickerItems(bracket, event)
  if (items.length === 0) return null

  // Duplicate for seamless loop.
  const doubled = [...items, ...items]

  return (
    // Flex sibling — sits below `<main>` in normal flow so phase
    // content can't scroll behind it. Previously absolute-positioned,
    // which caused the bottom of the focus card / podium to clip
    // beneath the ticker chrome.
    <footer
      className="flex h-[clamp(56px,7vh,84px)] shrink-0 items-center overflow-hidden border-t border-[var(--tv-border)] bg-[var(--tv-bg-elev)]"
      aria-label="Tournament ticker"
    >
      <div className="stadium-ticker-track flex shrink-0 items-center gap-[clamp(32px,3vw,56px)] pl-[clamp(20px,2.5vw,48px)]">
        {doubled.map((item, i) => (
          <TickerItem key={`${item.id}-${i}`} item={item} />
        ))}
      </div>
    </footer>
  )
}

function TickerItem({ item }: { item: TickerEntry }) {
  const accent =
    item.kind === 'NEXT'
      ? 'var(--tv-primary)'
      : 'var(--tv-fg-muted)'
  return (
    <span className="flex shrink-0 items-center gap-[clamp(10px,1vw,16px)]">
      <span
        className="font-display font-extrabold uppercase"
        style={{
          color: accent,
          letterSpacing: '0.22em',
          fontSize: 'clamp(0.7rem, 0.85vw, 0.9rem)',
        }}
      >
        {item.kind === 'NEXT' ? 'Next' : 'Result'}
      </span>
      <span
        className="font-display font-semibold"
        style={{
          color: 'var(--tv-fg)',
          fontSize: 'clamp(1rem, 1.15vw, 1.25rem)',
          letterSpacing: '0.02em',
        }}
      >
        {item.label}
      </span>
    </span>
  )
}

interface TickerEntry {
  id: string
  kind: 'NEXT' | 'RESULT'
  label: string
  /** ISO/ms — used for sort. */
  ts: number
}

/**
 * Derive ticker items from the bracket. Pure-ish — same input always
 * produces the same output, so React Query's polling re-renders are
 * cheap (the marquee is keyframe-driven and doesn't reset).
 */
function useTickerItems(
  bracket: Bracket | null,
  _event: RGNEvent | null,
): TickerEntry[] {
  return useMemo(() => {
    if (!bracket?.matches) return []
    const matches = bracket.matches
    const out: TickerEntry[] = []

    // NEXT UP — limit to the 6 soonest scheduled matches. Without a
    // cap, a 32-team tournament's worth of "scheduled" matches would
    // produce a 90-second loop that's all upcoming and zero results.
    const upcoming = matches
      .filter((m) => {
        const status = (m.status ?? '').toUpperCase()
        return status === 'SCHEDULED' || status === 'AWAITING_CONFIRMATION' || status === 'PROPOSED'
      })
      .filter((m) => m.participantA && m.participantB)
      .map((m) => ({
        match: m,
        ts: toMs(m.scheduledAt) ?? Number.MAX_SAFE_INTEGER,
      }))
      .sort((a, b) => a.ts - b.ts)
      .slice(0, 6)

    for (const { match, ts } of upcoming) {
      const a = teamDisplayName(match.participantA)
      const b = teamDisplayName(match.participantB)
      const when = ts !== Number.MAX_SAFE_INTEGER ? ` · ${formatWhen(ts)}` : ''
      out.push({
        id: `next-${match.matchId}`,
        kind: 'NEXT',
        label: `${a} vs ${b}${when}`,
        ts,
      })
    }

    // RESULTS — most recent 8 completed matches, newest first. Read
    // winner + opponent from the bracket row; no game-by-game score
    // because the ticker is for at-a-glance context, not analysis.
    const completed = matches
      .filter((m) => {
        const status = (m.status ?? '').toUpperCase()
        return status === 'COMPLETE' || status === 'COMPLETED' || status === 'OVERRIDDEN'
      })
      .map((m) => ({
        match: m,
        ts: toMs((m as { completedAt?: string | number }).completedAt)
            ?? toMs(m.scheduledAt) ?? 0,
      }))
      .sort((a, b) => b.ts - a.ts)
      .slice(0, 8)

    for (const { match, ts } of completed) {
      const winnerId = match.winnerTeamId
      const winner =
        match.participantA?.teamId === winnerId ? match.participantA :
        match.participantB?.teamId === winnerId ? match.participantB :
        null
      const loser =
        match.participantA?.teamId === winnerId ? match.participantB :
        match.participantB?.teamId === winnerId ? match.participantA :
        null
      if (!winner || !loser) continue
      const scoreLine = scoreFromGames(match.gameResults, winner.teamId)
      const score = scoreLine ? ` ${scoreLine}` : ''
      out.push({
        id: `result-${match.matchId}`,
        kind: 'RESULT',
        label: `${teamDisplayName(winner)} beat ${teamDisplayName(loser)}${score}`,
        ts,
      })
    }

    return out
  }, [bracket])
}

function scoreFromGames(
  games: Bracket['matches'][number]['gameResults'] | undefined,
  winnerTeamId: string | undefined,
): string | null {
  if (!games || games.length === 0) return null
  if (!winnerTeamId) return null
  let won = 0
  let lost = 0
  for (const g of games) {
    if (g.winnerTeamId === winnerTeamId) won++
    else if (g.winnerTeamId) lost++
  }
  if (won + lost === 0) return null
  return `${won}-${lost}`
}

function toMs(v: number | string | null | undefined): number | null {
  if (v == null) return null
  if (typeof v === 'number') return v
  const parsed = Date.parse(v)
  return Number.isFinite(parsed) ? parsed : null
}

function formatWhen(ts: number): string {
  const diffMs = ts - Date.now()
  if (diffMs < 0) return 'now'
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'now'
  if (minutes < 60) return `in ${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remMins = minutes % 60
  return remMins === 0 ? `in ${hours}h` : `in ${hours}h ${remMins}m`
}
